import { motion } from 'motion/react';
import { GraduationCap, Scale, Home, Sparkles, Heart, Building2, Wine, Star, ArrowRight } from 'lucide-react';
import { useTranslation } from '../i18n';

const services = [
  {
    icon: GraduationCap,
    titleKey: 'servicesGrid.services.education.title',
    subtitleKey: 'servicesGrid.services.education.subtitle',
    descriptionKey: 'servicesGrid.services.education.description',
    navAction: 'education',
  },
  {
    icon: Scale,
    titleKey: 'servicesGrid.services.legal.title',
    subtitleKey: 'servicesGrid.services.legal.subtitle',
    descriptionKey: 'servicesGrid.services.legal.description',
    navAction: 'legal',
  },
  {
    icon: Home,
    titleKey: 'servicesGrid.services.realEstate.title',
    subtitleKey: 'servicesGrid.services.realEstate.subtitle',
    descriptionKey: 'servicesGrid.services.realEstate.description',
    navAction: 'realEstate',
  },
  {
    icon: Sparkles,
    titleKey: 'servicesGrid.services.homeManagement.title',
    subtitleKey: 'servicesGrid.services.homeManagement.subtitle',
    descriptionKey: 'servicesGrid.services.homeManagement.description',
    navAction: 'homeManagement',
  },
  {
    icon: Heart,
    titleKey: 'servicesGrid.services.spiritual.title',
    subtitleKey: 'servicesGrid.services.spiritual.subtitle',
    descriptionKey: 'servicesGrid.services.spiritual.description',
    navAction: 'spiritual',
  },
  {
    icon: Building2,
    titleKey: 'servicesGrid.services.business.title',
    subtitleKey: 'servicesGrid.services.business.subtitle',
    descriptionKey: 'servicesGrid.services.business.description',
    navAction: 'business',
  },
  {
    icon: Wine,
    titleKey: 'servicesGrid.services.beverage.title',
    subtitleKey: 'servicesGrid.services.beverage.subtitle',
    descriptionKey: 'servicesGrid.services.beverage.description',
    navAction: 'beverage',
  },
  {
    icon: Star,
    titleKey: 'servicesGrid.services.elite.title',
    subtitleKey: 'servicesGrid.services.elite.subtitle',
    descriptionKey: 'servicesGrid.services.elite.description',
    navAction: 'elite',
  },
];

interface ServicesGridProps {
  onNavigateToServices: () => void;
  onNavigateToSchoolSupport: () => void;
  onNavigateToLegalSupport: () => void;
  onNavigateToRealEstate: () => void;
  onNavigateToHomeManagement: () => void;
  onNavigateToSpiritualWellness: () => void;
  onNavigateToBusinessConsulting: () => void;
  onNavigateToBeverageDevelopment: () => void;
  onNavigateToEliteConcierge: () => void;
}

export function ServicesGrid({ 
  onNavigateToServices, 
  onNavigateToSchoolSupport,
  onNavigateToLegalSupport,
  onNavigateToRealEstate,
  onNavigateToHomeManagement,
  onNavigateToSpiritualWellness,
  onNavigateToBusinessConsulting,
  onNavigateToBeverageDevelopment,
  onNavigateToEliteConcierge
}: ServicesGridProps) {
  const { t } = useTranslation();

  // Map service nav actions to their navigation functions
  const getNavigationHandler = (navAction: string) => {
    switch (navAction) {
      case 'education':
        return onNavigateToSchoolSupport;
      case 'legal':
        return onNavigateToLegalSupport;
      case 'realEstate':
        return onNavigateToRealEstate;
      case 'homeManagement':
        return onNavigateToHomeManagement;
      case 'spiritual':
        return onNavigateToSpiritualWellness;
      case 'business':
        return onNavigateToBusinessConsulting;
      case 'beverage':
        return onNavigateToBeverageDevelopment;
      case 'elite':
        return onNavigateToEliteConcierge;
      default:
        return onNavigateToServices;
    }
  };

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-[#0A1A33] mb-4 font-bold font-normal text-[36px]">{t('servicesGrid.section.title')}</h2>
          <p className="text-gray-600">{t('servicesGrid.section.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.titleKey}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ y: -5 }}
              className="group bg-white rounded-xl p-6 border border-gray-200 hover:border-[#C6A667] hover:shadow-lg transition-all duration-300 cursor-pointer flex flex-col"
              onClick={getNavigationHandler(service.navAction)}
            >
              <div className="mb-4 flex items-center justify-center w-12 h-12 bg-[#0A1A33]/5 group-hover:bg-[#C6A667]/10 rounded-lg transition-colors duration-300">
                <service.icon className="w-6 h-6 text-[#C6A667]" />
              </div>
              
              <h4 className="text-[#0A1A33] mb-1 font-bold text-[15px]">{t(service.titleKey)}</h4>
              <p className="text-xs text-gray-500 mb-3">{t(service.subtitleKey)}</p>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed flex-grow">{t(service.descriptionKey)}</p>
              
              <button className="group/btn flex items-center gap-1 text-sm text-[#C6A667] hover:text-[#D4B578] transition-colors">
                <span className="font-bold no-underline">{t('servicesGrid.cta.learnMore')}</span>
                <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          {/* CTA button removed */}
        </motion.div>
      </div>
    </section>
  );
}
